import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { MainLayout } from 'views/layouts'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import Box from '@mui/material/Box'
import Stepper from 'views/components/Stepper'
import Select from 'views/components/UI/Select'
import MultiSelect from 'views/components/UI/SelectMultiple/select-multiple'
import Input from 'views/components/UI/TextInput'
import { P } from 'views/components/UI/Text'
import { Button, BackButton } from 'views/components/UI/Buttons'
import { InputGrid, Row, Container, Error, TitleContainer, SmallGrayText } from './styled'
import InputRadio from 'views/components/UI/InputRadio'
import ModalLoader from 'views/components/UI/ModalLoader'
import { useNotification } from 'lib/context/notification.context'
import axios from 'lib/axios'
import { platformAdminRoutes } from 'router/routes'
import { setStepOneTrigger } from 'ducks/createProcess'
import { STEPS_NEW_PROCESS } from './constants'
import { generalDataSelector } from 'ducks/auth'
import { userSelector } from 'ducks/user'
import { stepOneSelector, loadingCreateProcessSelector } from 'ducks/createProcess'
import { StepOne, LevelType, InventoriesAvailables as InventoriesAvailable } from 'types/common'
import { StepOneSchema } from './validation-schemas'
import getInventoriesByCompany from 'services/companies/getInventories'
import getSubscriptionByProcess from 'services/companies/getSubscriptionByProcess'
import useAdminsByCompany from 'hooks/company/useGetAdminsByCompany/useGetAdminsByCompany'
import { parseInventoryNameToId, parseInventoryIdToName } from './main'
import { BETESA_DEFAULT_CATEGORY_ID } from './constants'
import { v4 as uuidv4 } from 'uuid'
import getOneCompany from 'services/companies/getOneCompany'
import { setCompanyData } from 'ducks/company/reducer'

type QuestionnaireOption = {
  value: number | string
  label: string
}

const NO_QUESTIONNAIRE_VALUE = '__no_questionnaire__'

function CreateProcess() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { adminsByCompany, loading: loadingAdmins } = useAdminsByCompany()
  const { getError } = useNotification()

  const { data: generalData } = useSelector(generalDataSelector)
  const levelTypes = generalData?.level
    ?.filter((item: any) => item.id !== BETESA_DEFAULT_CATEGORY_ID)
    .map((item: any) => ({ value: item.id, label: item.name, id: item.id }))

  const { companyId } = useSelector(userSelector)
  const role = useSelector((state: any) => state.auth.role)
  const stepOneData = useSelector(stepOneSelector)
  const loadingCreateProcess = useSelector(loadingCreateProcessSelector)
  const {
    processType,
    processName,
    inventoryTypeId,
    inventoryUniqName,
    level,
    admins,
    automaticReports,
    testLanguage,
    permitDevelopmentPlan,
    processId,
    profileId,
    showInputId,
    companyQuestionnaireId,
  }: StepOne = stepOneData

  const [loading, setLoading] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [inventoriesAvailable, setInventoriesAvailables] = useState<InventoriesAvailable[]>([])
  const [levels, setLevels] = useState<LevelType[]>([])
  const [IdInventory, setIdInventory] = useState<number | null>(null)
  const [inventoryTypeIdState, setInventoryTypeIdState] = useState<number | undefined>()
  const [allowIntegration, setAllowIntegration] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const [questionnaireOptions, setQuestionnaireOptions] = useState<QuestionnaireOption[]>([
    { value: NO_QUESTIONNAIRE_VALUE, label: 'Sin cuestionario' },
  ])
  const submitRef = useRef<((isSubmitting: boolean) => void) | null>(null)
  const { getWarning } = useNotification()
  const dataUser = useSelector((state: any) => state.user)
  const canAssignQuestionnaire =
    role !== 'platform_admin' &&
    (role === 'super_admin' || Boolean(dataUser.hasQuestionnaireManagement))
  useEffect(() => {
    localStorage.removeItem('aiSkills')
    localStorage.removeItem('modelSkills')
    localStorage.removeItem('origin')
    getOneCompany(companyId)
      .then((response) => {
        if (response.status === 'OK') {
          setAllowIntegration(response.data.allow_integration)
          dispatch(setCompanyData(response.data))
        }
      })
      .catch(() => {
        getError('Ha ocurrido un error al intentar obtener la información de la compañía')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  useEffect(() => {
    if (!canAssignQuestionnaire) return

    axios
      .get('/company-questionnaire', {
        params: {
          status: 'published',
          page: 1,
          limit: 1000,
        },
      })
      .then((response) => {
        const questionnaires = response?.data?.questionnaires || []

        setQuestionnaireOptions([
          { value: NO_QUESTIONNAIRE_VALUE, label: 'Sin cuestionario' },
          ...questionnaires.map((questionnaire: any) => ({
            value: questionnaire.id,
            label: questionnaire.name,
          })),
        ])
      })
      .catch(() => {
        setQuestionnaireOptions([{ value: NO_QUESTIONNAIRE_VALUE, label: 'Sin cuestionario' }])
        getError('Ha ocurrido un error al intentar obtener los cuestionarios publicados')
      })
  }, [canAssignQuestionnaire, getError])

  const [currentProcessType, setCurrentProcessType] = useState(processType || 0)

  const inventoryTypes = useMemo(() => {
    if (!Array.isArray(inventoriesAvailable)) return []

    let filteredInventories = inventoriesAvailable

    if (currentProcessType === 1) {
      filteredInventories = inventoriesAvailable.filter((item: any) => item.subscriptionId !== 5)
    }

    return filteredInventories.map((item: any) => ({
      value: item.subscriptionName,
      label:
        item.subscriptionId === 5 ? (
          <>
            {item.subscriptionName} <SmallGrayText>(360°)</SmallGrayText>
          </>
        ) : (
          item.subscriptionName
        ),
    }))
  }, [inventoriesAvailable, currentProcessType])

  const adminOptions =
    !loadingAdmins && adminsByCompany && adminsByCompany.length > 0
      ? [
          { value: 0, label: 'Todos' },
          ...adminsByCompany
            .filter((item: any) => item.user_id !== dataUser.userId)
            .map((item: any) => ({ value: item.user_id, label: item.full_name })),
        ]
      : []

  const { handleSubmit, handleChange, values, setFieldValue, errors, isSubmitting, setValues } = useFormik({
    initialValues: {
      processType: processType || 0,
      processName: processName || '',
      inventoryType: '',
      level: level || '',
      admins: admins || [],
      automaticReports: automaticReports,
      testLanguage: testLanguage || 'ES',
      userId: dataUser.userId,
      permitDevelopmentPlan: permitDevelopmentPlan,
      profileId: profileId || '',
      showInputId: showInputId || false,
      showCustomIdOption: showInputId || false,
      companyQuestionnaireId: companyQuestionnaireId ?? NO_QUESTIONNAIRE_VALUE,
    },
    validationSchema: StepOneSchema,
    onSubmit: async (valuesForm, { setSubmitting }) => {
      try {
        submitRef.current = setSubmitting
        setSubmitting(true)
        setLoading(true)
        const selectedLevelObject = levels.find((levelData) => String(levelData.value) === valuesForm.level.toString())
        const levelName = selectedLevelObject ? selectedLevelObject.label : ''
        const filteredAdmins = valuesForm.admins.filter((admin: any) => admin !== 0)
        const dataStepOneToDispatch: any = {
          ...valuesForm,
          admins: filteredAdmins,
          profileId: valuesForm.profileId === '' ? uuidv4() : valuesForm.profileId,
          inventoryType: parseInventoryNameToId(inventoriesAvailable, valuesForm.inventoryType as any),
          companyId,
          inventoryUniqName: valuesForm.inventoryType,
          inventoryId: IdInventory,
          inventoryTypeId: IdInventory,
          levelName: levelName,
          companyQuestionnaireId:
            valuesForm.companyQuestionnaireId === NO_QUESTIONNAIRE_VALUE
              ? null
              : Number(valuesForm.companyQuestionnaireId),
        }

        if (processId) {
          dataStepOneToDispatch.processId = processId
        }
        setPendingNavigation(
          inventoryTypeIdState === 5
            ? platformAdminRoutes.SelectTestsType
            : platformAdminRoutes.PreconfigProcess
        )
        dispatch(setStepOneTrigger.run({ ...dataStepOneToDispatch }))
      } catch (error) {
        console.error('Error submiting the step one', error)
        setFieldValue('processName', '')
        getError('Ha ocurrido un error al intentar crear el paso 1 del proceso')
        submitRef.current?.(false)
        setLoading(false)
      }
    },
  })

  const handleInventoryType = useCallback(
    (inventoryName: string | number) => {
      if (inventoriesAvailable.length === 0) return

      const inventoryTypeIdSelected =
        typeof inventoryName === 'string'
          ? inventoriesAvailable.find((item: any) => item.subscriptionName === inventoryName)?.subscriptionId
          : inventoriesAvailable.find((item: any) => item.subscription_company_id === inventoryName)?.subscriptionId

      const inventoryIdSelected: number =
        typeof inventoryName === 'number'
          ? inventoryName
          : inventoriesAvailable.find((item: InventoriesAvailable) => item.subscriptionName === inventoryName)
              ?.subscription_company_id || 0

      setIdInventory(inventoryIdSelected)
      setInventoryTypeIdState(inventoryTypeIdSelected)

      switch (inventoryTypeIdSelected) {
        case 1:
          setLevels(levelTypes?.filter((item: any) => item.id === 4))
          break
        case 2:
          setLevels(levelTypes?.filter((item: any) => item.id === 2 || item.id === 3))
          break
        case 3:
        case 4:
          setLevels(levelTypes?.filter((item: any) => item.id === 2 || item.id === 3 || item.id === 4))
          break
        case 5:
          setLevels(levelTypes?.filter((item: any) => item.id === 2 || item.id === 3))
          break
        default:
          getWarning('No se encontró el tipo de inventario')
          break
      }
    },
    [levelTypes, inventoriesAvailable, getWarning]
  )

  useEffect(() => {
    if (loading) return
    setLoading(true)

    // Siempre cargar todos los inventarios disponibles independientemente de si hay processId o no
    getInventoriesByCompany({ companyId, status: true, hasCredits: true, limit: 1000 })
      .then((response) => {
        if (response.status === 'OK') {
          setInventoriesAvailables(response.data as InventoriesAvailable[])

          // Si hay un processId, también obtener la suscripción específica para preseleccionarla
          if (processId) {
            getSubscriptionByProcess(processId)
              .then((subscriptionResponse: any) => {
                if (subscriptionResponse.status === 'success') {
                  // No sobrescribimos los inventarios disponibles, solo usamos esta información
                  // para preseleccionar el inventario correcto en el formulario
                  const inventory = subscriptionResponse.data
                  console.log('Preseleccionando inventario:', inventory.subscriptionName)
                }
              })
              .catch((error) => {
                console.error('Error al obtener la suscripción del proceso:', error)
              })
          }
        } else {
          getError('Esta compañía no tiene inventarios disponibles')
          setInventoriesAvailables([])
        }
      })
      .catch(() => {
        getError('Ha ocurrido un error al intentar obtener los inventarios disponibles')
        setInventoriesAvailables([])
      })
      .finally(() => {
        setLoading(false)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryTypeId, processName, processId])

  useEffect(() => {
    setValues({
      processType: processType || 0,
      processName: processName || '',
      inventoryType: inventoryUniqName || parseInventoryIdToName(inventoriesAvailable, inventoryTypeId as any) || '',
      level: level || '',
      admins: admins || [],
      automaticReports: automaticReports,
      testLanguage: testLanguage || 'ES',
      userId: dataUser.userId,
      permitDevelopmentPlan: permitDevelopmentPlan,
      profileId: profileId || '',
      showCustomIdOption: showInputId || false,
      showInputId: showInputId || false,
      companyQuestionnaireId: companyQuestionnaireId ?? NO_QUESTIONNAIRE_VALUE,
    })
    // Sincronizar el estado local del processType
    setCurrentProcessType(processType || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    processType,
    processName,
    inventoryTypeId,
    level,
    admins,
    automaticReports,
    testLanguage,
    permitDevelopmentPlan,
    setValues,
    inventoryUniqName,
    stepOneData,
    inventoriesAvailable,
    profileId,
    showInputId,
    companyQuestionnaireId,
  ])

  useEffect(() => {
    if (inventoryUniqName && inventoriesAvailable.length > 0) {
      handleInventoryType(inventoryUniqName)
    } else if (inventoryTypeId && inventoriesAvailable.length > 0) {
      handleInventoryType(inventoryTypeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryTypeId, loadingCreateProcess, inventoryUniqName, inventoriesAvailable])

  useEffect(() => {
    if (!pendingNavigation || loadingCreateProcess || !processId) return

    navigate(pendingNavigation)
    setPendingNavigation(null)
    submitRef.current?.(false)
    setLoading(false)
  }, [loadingCreateProcess, navigate, pendingNavigation, processId])

  return (
    <MainLayout>
      {!loadingCreateProcess && !loading ? (
        <Container>
          <TitleContainer>
            <h2>Configuración del proceso</h2>
            <Box sx={{ width: '100%' }}>
              <Stepper activeStep={0} steps={STEPS_NEW_PROCESS} />
            </Box>
          </TitleContainer>
          {inventoriesAvailable?.length > 0 ? (
            <>
              <form onSubmit={handleSubmit}>
                <Row margin="20px 0px 8px 0px">
                  <P>¿Es un proceso de selección o desarrollo?</P>
                  <InputRadio
                    checked={values.processType === 1}
                    label="Selección"
                    value="Seleccion"
                    onChange={() => {
                      setFieldValue('processType', 1)
                      setCurrentProcessType(1)

                      setFieldValue('inventoryType', '')
                    }}
                    name="processType"
                    tooltipText="Esta opción permite crear un proceso de selección de personal, en el cual se evaluarán las competencias de los candidatos a un cargo específico."
                  />
                  <InputRadio
                    checked={values.processType === 2}
                    label="Desarrollo"
                    value="Desarrollo"
                    onChange={() => {
                      setFieldValue('processType', 2)
                      setCurrentProcessType(2)

                      setFieldValue('inventoryType', '')
                    }}
                    name="processType"
                    tooltipText="Esta opción permite crear un proceso de desarrollo de personal, en el cual se evaluarán las competencias de los empleados de una organización."
                  />
                </Row>
                {showErrors && errors.processType && <Error>{errors.processType}</Error>}
                <InputGrid style={{ marginBottom: '20px' }}>
                  <Input
                    value={values.processName}
                    onChange={handleChange}
                    type="text"
                    name="processName"
                    label="Nombre del proceso"
                    helperText={(showErrors && errors?.processName) || ''}
                  />
                  <Select
                    label="Inventario a utilizar"
                    options={inventoryTypes}
                    value={values.inventoryType}
                    onChange={(e) => {
                      setFieldValue('inventoryType', e)
                      handleInventoryType(e)
                      setFieldValue('showCustomIdOption', false)
                      setFieldValue('permitDevelopmentPlan', false)
                    }}
                    name="inventoryType"
                    error={(showErrors && (errors.inventoryType as string)) || ''}
                    hideNeverChoice
                    disabled={values.processType === 0}
                  />
                  <Select
                    width="98%"
                    label="Nivel"
                    options={levels}
                    value={values.level}
                    onChange={(e) => {
                      setFieldValue('level', e)
                    }}
                    name="level"
                    error={(showErrors && (errors.level as string)) || ''}
                    hideNeverChoice
                    disabled={values.inventoryType === ''}
                  />
                  {canAssignQuestionnaire ? (
                    <Select
                      width="98%"
                      label="Asignar cuestionario"
                      options={questionnaireOptions}
                      value={values.companyQuestionnaireId}
                      onChange={(value) => {
                        setFieldValue('companyQuestionnaireId', value)
                      }}
                      name="companyQuestionnaireId"
                      hideNeverChoice
                    />
                  ) : null}
                  <Row>
                    <MultiSelect
                      maxWidth="none"
                      width="100%"
                      label="Administradores que podrán ver este proceso"
                      options={adminOptions}
                      value={values.admins}
                      onChange={(e) => {
                        const newValues = e
                        const currentValues = values.admins

                        if (newValues.includes(0) && !currentValues.includes(0)) {
                          setFieldValue(
                            'admins',
                            adminOptions.map((user) => user.value)
                          )
                        } else if (!newValues.includes(0) && currentValues.includes(0)) {
                          setFieldValue('admins', [])
                        } else if (newValues.length < currentValues.length) {
                          setFieldValue(
                            'admins',
                            newValues.filter((value: number | string) => value !== 0)
                          )
                        } else {
                          setFieldValue('admins', newValues)
                        }
                      }}
                      error={(showErrors && (errors.admins as string)) || ''}
                    />
                  </Row>
                </InputGrid>
                <Row>
                  <P width="45%">¿Entrega automática de los reportes a los participantes?</P>
                  <InputRadio
                    checked={values.automaticReports}
                    label="Si"
                    value="Si"
                    onChange={() => setFieldValue('automaticReports', true)}
                    name="automaticReports"
                  />
                  <InputRadio
                    checked={values.automaticReports === (false as any)}
                    label="No"
                    value="No"
                    onChange={() => setFieldValue('automaticReports', false)}
                    name="automaticReports"
                  />
                </Row>
                {showErrors && errors.automaticReports && (
                  <div style={{ margin: '12px 0px' }}>
                    <Error>{errors.automaticReports}</Error>
                  </div>
                )}
                <Row>
                  <P width="45%">¿Permitir creación de plan de desarrollo por usuario?</P>
                  <InputRadio
                    checked={values.permitDevelopmentPlan}
                    label="Si"
                    value="Si"
                    onChange={() => setFieldValue('permitDevelopmentPlan', true)}
                    name="permitDevelopmentPlan"
                    disabled={inventoryTypeIdState === 5}
                  />
                  <InputRadio
                    checked={values.permitDevelopmentPlan === (false as any)}
                    label="No"
                    value="No"
                    onChange={() => setFieldValue('permitDevelopmentPlan', false)}
                    name="permitDevelopmentPlan"
                    disabled={inventoryTypeIdState === 5}
                  />
                </Row>
                {showErrors && errors.permitDevelopmentPlan && (
                  <div style={{ margin: '12px 0px' }}>
                    <Error>{errors.permitDevelopmentPlan}</Error>
                  </div>
                )}
                {allowIntegration ? (
                  <>
                    <Row style={{ marginTop: '10px', marginBottom: '-8px' }}>
                      <P width="45%">Integración:</P>
                    </Row>
                    <Row>
                      <P width="45%">¿Quieres activar este proceso para integración?</P>
                      <InputRadio
                        checked={values.showCustomIdOption}
                        label="Si"
                        value="Si"
                        onChange={() => setFieldValue('showCustomIdOption', true)}
                        name="showInputId"
                        disabled={inventoryTypeIdState === 5}
                      />
                      <InputRadio
                        checked={!values.showCustomIdOption}
                        label="No"
                        value="No"
                        onChange={() => setFieldValue('showCustomIdOption', false)}
                        name="showInputId"
                        disabled={inventoryTypeIdState === 5}
                      />
                    </Row>
                  </>
                ) : null}
                {values.showCustomIdOption ? (
                  <>
                    <Row>
                      <P width="45%">¿Tienes un identificador de perfil personalizado?</P>
                      <InputRadio
                        checked={values.showInputId}
                        label="Si"
                        value="Si"
                        onChange={() => setFieldValue('showInputId', true)}
                        name="showInputId"
                      />
                      <InputRadio
                        checked={!values.showInputId}
                        label="No"
                        value="No"
                        onChange={() => setFieldValue('showInputId', false)}
                        name="showInputId"
                      />
                    </Row>
                    {values.showInputId && (
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Input
                          value={values.profileId}
                          onChange={handleChange}
                          style={{ width: '10%', marginLeft: '30px' }}
                          type="number"
                          name="profileId"
                          label="Id del perfil"
                          helperText={(showErrors && errors?.profileId) || ''}
                        />
                      </div>
                    )}
                  </>
                ) : null}
                <Row
                  margin="16px 0px"
                  justifyContent="space-between"
                  style={{
                    width: 'calc(100% - 40px)',
                    padding: '0 20px',
                    alignItems: 'flex-end',
                    position: 'absolute',
                    bottom: '20px',
                    left: '0',
                    right: '0',
                  }}
                >
                  <BackButton onClick={() => navigate(platformAdminRoutes.Home)} />
                  <Button
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                    onDisabledClick={() => {
                      setShowErrors(true)
                      getError('Por favor, completa los campos requeridos')
                    }}
                    type="submit"
                  >
                    Siguiente
                  </Button>
                </Row>
              </form>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
              }}
            >
              <h3 style={{ color: 'rgb(198, 1, 107)' }}>Esta compañía no tiene inventarios asociados disponibles</h3>
            </div>
          )}
        </Container>
      ) : (
        <ModalLoader isLoading={loadingCreateProcess || loading} />
      )}
    </MainLayout>
  )
}

export default CreateProcess
